/**
 * Updates the recently viewed products in localStorage.
 */
export class RecentlyViewed {
  /** @static @constant {string} The key used to store the viewed products in localStorage */
  static #STORAGE_KEY = 'viewedProducts';
  /** @static @constant {number} The maximum number of products to store */
  static #MAX_PRODUCTS = 12;

  /**
   * @typedef {Object} RecentlyViewedProductData
   * @property {string} id
   * @property {string} [variantId]
   * @property {string} [title]
   * @property {string} [url]
   * @property {string} [image]
   * @property {string} [price]
   */

  /**
   * @typedef {Object} RecentlyViewedState
   * @property {string[]} ids
   * @property {{ [key: string]: RecentlyViewedProductData }} products
   */

  /**
   * @param {string | number | null | undefined} id
   * @returns {string}
   */
  static #sanitizeId(id) {
    return String(id ?? '').trim();
  }

  /**
   * @returns {RecentlyViewedState}
   */
  static #readState() {
    try {
      const parsed = JSON.parse(localStorage.getItem(this.#STORAGE_KEY) || '[]');
      if (Array.isArray(parsed)) {
        return {
          ids: parsed.map((id) => this.#sanitizeId(id)).filter(Boolean),
          products: {}
        };
      }

      if (parsed && typeof parsed === 'object') {
        const ids = Array.isArray(parsed.ids) ? parsed.ids.map((id) => this.#sanitizeId(id)).filter(Boolean) : [];
        const products = parsed.products && typeof parsed.products === 'object' ? parsed.products : {};
        return { ids, products };
      }
    } catch {
      // ignore storage parse issues
    }

    return { ids: [], products: {} };
  }

  /**
   * @param {RecentlyViewedState} state
   */
  static #writeState(state) {
    try {
      localStorage.setItem(this.#STORAGE_KEY, JSON.stringify(state));
    } catch {
      // ignore storage write issues
    }
  }

  /**
   * @param {string} productId
   * @param {Partial<RecentlyViewedProductData> | null} productData
   * @returns {RecentlyViewedProductData | null}
   */
  static #normalizeProductData(productId, productData) {
    if (!productData || typeof productData !== 'object') return null;
    const id = this.#sanitizeId(productId);
    if (!id) return null;

    const normalized = {
      id,
      variantId: this.#sanitizeId(productData.variantId),
      title: String(productData.title || '').trim(),
      url: String(productData.url || '').trim(),
      image: String(productData.image || '').trim(),
      price: String(productData.price || '').trim()
    };

    return normalized.url ? normalized : null;
  }

  /**
   * Adds a product to the recently viewed products list.
   * @param {string} productId - The ID of the product to add.
   * @param {Partial<RecentlyViewedProductData>} [productData]
   */
  static addProduct(productId, productData = null) {
    const id = this.#sanitizeId(productId);
    if (!id) return;

    const state = this.#readState();
    state.ids = state.ids.filter((storedId) => storedId !== id);
    state.ids.unshift(id);
    state.ids = state.ids.slice(0, this.#MAX_PRODUCTS);

    const normalized = this.#normalizeProductData(id, productData);
    if (normalized) {
      state.products[id] = normalized;
    }

    Object.keys(state.products).forEach((storedId) => {
      if (!state.ids.includes(storedId)) {
        delete state.products[storedId];
      }
    });

    this.#writeState(state);
  }

  static clearProducts() {
    localStorage.removeItem(this.#STORAGE_KEY);
  }

  /**
   * Retrieves the list of recently viewed products from session storage.
   * @returns {string[]} The list of viewed products.
   */
  static getProducts() {
    return this.#readState().ids;
  }

  /**
   * Retrieves the map of recently viewed products metadata.
   * @returns {{ [key: string]: RecentlyViewedProductData }}
   */
  static getProductsMap() {
    return this.#readState().products;
  }

  /**
   * Retrieves one recently viewed product metadata by id.
   * @param {string} productId
   * @returns {RecentlyViewedProductData | null}
   */
  static getProductData(productId) {
    const id = this.#sanitizeId(productId);
    if (!id) return null;
    return this.#readState().products[id] || null;
  }
}
